import { Rng } from '../../shared';
export type Terrain = 'moss' | 'stone' | 'puddle';
export type Pace = 'sprint' | 'cruise' | 'graze';
export type RaceLane = 0 | 1;
export const SNAILS = ['moss', 'stone', 'puddle'] as const;
export interface Racer { snail: Terrain; distance: number; stamina: number; lane: RaceLane }
export interface Race { id: string; seed: string; sector: number; course: [Terrain, Terrain][]; racers: [Racer, Racer, Racer]; status: 'racing' | 'finished'; place: number | null; claimed: boolean; gains: [number, number, number]; rewardLines?: Array<{ key: string; params: Record<string, string | number> }> }
export function newRace(seed: string, index: number, snail: Terrain): Race {
  const key = `${seed}:snails:${index}`, rng = new Rng(key);
  const course: [Terrain, Terrain][] = Array.from({ length: 8 }, () => {
    const a = rng.pick(SNAILS), b = rng.pick(SNAILS.filter(t => t !== a)); return [a, b];
  });
  return { id: key, seed: key, sector: 0, course, racers: [snail, 'stone', 'puddle'].map(snail => ({ snail: snail as Terrain, distance: 0, stamina: 6, lane: 0 })) as Race['racers'], status: 'racing', place: null, claimed: false, gains: [0, 0, 0] };
}
export function paceAllowed(r: Racer, pace: Pace): boolean { return pace !== 'sprint' || r.stamina >= 3; }
export function paceGain(r: Racer, pace: Pace, terrain: Terrain): number {
  return (pace === 'sprint' ? 9 : pace === 'cruise' ? 5 : 2) + (r.snail === terrain ? 2 : 0) + (terrain === 'puddle' && pace === 'cruise' ? 1 : 0) - (terrain === 'stone' && pace === 'sprint' ? 2 : 0);
}
export function raceGain(race: Race, index: number, lane: RaceLane, pace: Pace): number {
  const racer = race.racers[index];
  const drafting = pace !== 'sprint' && race.racers.some((other, j) => j !== index && other.lane === lane && other.distance > racer.distance && other.distance - racer.distance <= 6) ? 2 : 0;
  return paceGain(racer, pace, race.course[race.sector][lane]) + drafting;
}
export function raceStep(race: Race, lane: RaceLane, pace: Pace): Race {
  if (race.status !== 'racing' || (lane !== 0 && lane !== 1) || !['sprint', 'cruise', 'graze'].includes(pace) || !paceAllowed(race.racers[0], pace)) return race;
  const next = structuredClone(race), terrain = race.course[race.sector];
  const decisions: Array<{ lane: RaceLane; pace: Pace }> = [{ lane, pace }];
  for (const i of [1, 2] as const) {
    const r = race.racers[i], last = race.sector >= 6;
    const picks = ([0, 1] as const).flatMap(lane => (['sprint', 'cruise', 'graze'] as const).filter(p => paceAllowed(r, p)).map(pace => {
      const staminaValue = last ? 0.15 : 0.8;
      const staminaDelta = pace === 'sprint' ? -3 : pace === 'graze' ? Math.min(9 - r.stamina, 3 + (terrain[lane] === 'moss' ? 1 : 0)) : Math.min(9 - r.stamina, 1);
      return { lane, pace, utility: paceGain(r, pace, terrain[lane]) + staminaDelta * staminaValue };
    }));
    picks.sort((a, b) => b.utility - a.utility); decisions.push(picks[0]);
  }
  decisions.forEach((d, i) => {
    const r = next.racers[i], ground = terrain[d.lane];
    const gain = raceGain(race, i, d.lane, d.pace);
    r.distance += gain; r.lane = d.lane;
    r.stamina = Math.min(9, r.stamina + (d.pace === 'sprint' ? -3 : d.pace === 'graze' ? 3 + (ground === 'moss' ? 1 : 0) : 1));
    next.gains[i] = gain;
  });
  next.sector++;
  if (next.sector >= next.course.length) { next.status = 'finished'; next.place = 1 + next.racers.filter(r => r.distance > next.racers[0].distance).length; }
  return next;
}
